/**
 * The one JSON error shape every API handler returns (ADR-013).
 * Shared so the client parses exactly what the Worker produces.
 */

export const API_ERROR_CODES = [
  'BAD_REQUEST',
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'CSRF_FAILED',
  'NOT_FOUND',
  'CONFLICT',
  'NOT_IMPLEMENTED',
  'METHOD_NOT_ALLOWED',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
  'NETWORK_ERROR',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export interface ApiErrorDetail {
  field: string;
  message: string;
}

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
    details?: ApiErrorDetail[];
  };
  correlationId: string;
}

export const CORRELATION_ID_HEADER = 'x-correlation-id';
export const CSRF_HEADER = 'x-csrf-token';
export const CSRF_COOKIE = 'ih_csrf';

function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === 'string' && (API_ERROR_CODES as readonly string[]).includes(value);
}

function isDetailArray(value: unknown): value is ApiErrorDetail[] {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as ApiErrorDetail).field === 'string' &&
        typeof (entry as ApiErrorDetail).message === 'string',
    )
  );
}

/** Narrows an unknown parsed body to the shared error shape. */
export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { error?: unknown; correlationId?: unknown };
  if (typeof candidate.correlationId !== 'string') return false;
  if (typeof candidate.error !== 'object' || candidate.error === null) return false;
  const error = candidate.error as { code?: unknown; message?: unknown; details?: unknown };
  if (!isApiErrorCode(error.code)) return false;
  if (typeof error.message !== 'string') return false;
  if (error.details !== undefined && !isDetailArray(error.details)) return false;
  return true;
}
